'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { ShoppingCart, Plus, Package, Truck, BarChart3, TrendingUp, TrendingDown, Clock, AlertTriangle, ChevronRight, Bell, DollarSign, RefreshCw, ClipboardList, ArrowUpRight, Receipt, FileSearch,  } from 'lucide-react';
import { AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,  } from 'recharts';
import { MOCK_REQUISITIONS, MOCK_PURCHASE_ORDERS, MOCK_INVOICES, MOCK_ALERTS, REQUISITION_STATUS_STYLES, PO_STATUS_STYLES, formatUGX,  } from './procurementData';
import { useRole } from '@/lib/useRole';
import Icon from '@/components/ui/AppIcon';


// ─── Spend Trend Data ─────────────────────────────────────────────────────────
const SPEND_TREND = [
  { month: 'Apr', spend: 18500000, orders: 8 },
  { month: 'May', spend: 22300000, orders: 11 },
  { month: 'Jun', spend: 19800000, orders: 9 },
  { month: 'Jul', spend: 31200000, orders: 14 },
  { month: 'Aug', spend: 27600000, orders: 12 },
  { month: 'Sep', spend: 31107500, orders: 4 },
];

const SPEND_BY_CATEGORY = [
  { name: 'Feed', value: 24700000, color: '#f59e0b' },
  { name: 'Vaccines', value: 3600000, color: '#10b981' },
  { name: 'PPE', value: 1807500, color: '#3b82f6' },
  { name: 'Chemicals', value: 1280000, color: '#8b5cf6' },
  { name: 'Other', value: 720000, color: '#6b7280' },
];

const SUPPLIER_SPEND = [
  { supplier: 'Ugachick', spend: 26507500 },
  { supplier: 'KVS', spend: 3600000 },
  { supplier: 'EA Agri', spend: 1280000 },
  { supplier: 'Nile Agro', spend: 720000 },
];

// ─── KPI Card ─────────────────────────────────────────────────────────────────
interface KpiCardProps {
  title: string;
  value: string | number;
  sub?: string;
  trend?: 'up' | 'down' | 'neutral';
  trendValue?: string;
  icon: React.ElementType;
  iconColor: string;
  href?: string;
  highlight?: boolean;
}

function KpiCard({ title, value, sub, trend, trendValue, icon: Icon, iconColor, href, highlight }: KpiCardProps) {
  const content = (
    <div className={`rounded-xl border p-4 flex flex-col gap-2 hover:shadow-md transition-all duration-200 h-full ${highlight ? 'border-amber-300 bg-amber-50/40' : 'border-border/60 bg-card shadow-sm'}`}>
      <div className="flex items-start justify-between">
        <div className={`h-8 w-8 rounded-lg flex items-center justify-center ${iconColor}`}>
          <Icon size={14} className="text-white" />
        </div>
        {href && <ChevronRight size={13} className="text-muted-foreground mt-0.5" />}
      </div>
      <div>
        <div className="text-xl font-semibold tracking-tight font-tabular text-foreground leading-tight">{value}</div>
        <div className="text-xs font-medium text-muted-foreground mt-0.5 leading-tight">{title}</div>
        {sub && <div className="text-xs text-muted-foreground mt-0.5 leading-tight">{sub}</div>}
      </div>
      {trend && trendValue && (
        <div className={`flex items-center gap-1 text-xs font-medium ${trend === 'up' ? 'text-green-600' : trend === 'down' ? 'text-red-500' : 'text-muted-foreground'}`}>
          {trend === 'up' ? <TrendingUp size={11} /> : trend === 'down' ? <TrendingDown size={11} /> : null}
          {trendValue}
        </div>
      )}
    </div>
  );
  if (href) return <Link href={href} className="block h-full">{content}</Link>;
  return content;
}

// ─── Alert Card ───────────────────────────────────────────────────────────────
function AlertCard({ alert }: { alert: typeof MOCK_ALERTS[0] }) {
  const colors = {
    info: { bg: 'bg-blue-50', border: 'border-blue-200', icon: 'text-blue-500', dot: 'bg-blue-500' },
    warning: { bg: 'bg-amber-50', border: 'border-amber-200', icon: 'text-amber-500', dot: 'bg-amber-500' },
    danger: { bg: 'bg-red-50', border: 'border-red-200', icon: 'text-red-500', dot: 'bg-red-500' },
  }[alert.severity];

  return (
    <div className={`flex items-start gap-3 p-3 rounded-xl border ${colors.bg} ${colors.border}`}>
      <span className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${colors.dot}`} />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-gray-800">{alert.title}</div>
        <div className="text-xs text-gray-500 mt-0.5 line-clamp-2">{alert.description}</div>
      </div>
      {alert.actionUrl && (
        <Link href={alert.actionUrl} className={`flex-shrink-0 text-xs font-semibold ${colors.icon} hover:underline`}>
          View
        </Link>
      )}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function ProcurementContent() {
  const { can } = useRole();
  const [refreshing, setRefreshing] = useState(false);

  const canManage = can('manage_procurement');
  const canView = can('view_procurement');

  const pendingApprovals = MOCK_REQUISITIONS.filter(r => r.status === 'pending_approval').length;
  const openPOs = MOCK_PURCHASE_ORDERS.filter(p => ['approved', 'issued', 'partially_delivered'].includes(p.status)).length;
  const totalSpend = MOCK_PURCHASE_ORDERS.reduce((s, p) => s + p.grandTotal, 0);
  const pendingInvoices = MOCK_INVOICES.filter(i => i.paymentStatus === 'unpaid').length;
  const partialDeliveries = MOCK_PURCHASE_ORDERS.filter(p => p.status === 'partially_delivered').length;

  if (!canView) {
    return (
      <div className="p-8 text-center">
        <ShoppingCart size={48} className="mx-auto text-gray-300 mb-4" />
        <h2 className="text-xl font-bold text-gray-700 mb-2">Access Restricted</h2>
        <p className="text-gray-500">You do not have permission to view the Procurement module.</p>
      </div>
    );
  }

  return (
    <div className="px-4 md:px-6 xl:px-8 py-6 max-w-screen-2xl mx-auto">

      {/* ── Header ── */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <ShoppingCart size={22} className="text-orange-600" />
            <h1 className="text-2xl font-bold text-gray-900">Procurement Management</h1>
          </div>
          <p className="text-sm text-gray-500 max-w-xl">
            Manage purchase requests, supplier sourcing, approvals, orders and goods receipt across your poultry operations.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={() => { setRefreshing(true); setTimeout(() => setRefreshing(false), 800); }} className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors" aria-label="Refresh">
            <RefreshCw size={15} className={refreshing ? 'animate-spin text-orange-500' : 'text-gray-500'} />
          </button>
          <Link href="/procurement/reports" className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors text-gray-700">
            <BarChart3 size={14} />
            Reports
          </Link>
          <Link href="/procurement/analytics" className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors text-gray-700">
            <TrendingUp size={14} />
            Analytics
          </Link>
          {canManage && (
            <Link href="/procurement/requisitions/new" className="flex items-center gap-1.5 px-4 py-2 bg-orange-600 text-white text-sm font-semibold rounded-xl hover:bg-orange-700 transition-colors shadow-sm">
              <Plus size={15} />
              New Requisition
            </Link>
          )}
        </div>
      </div>

      {/* ── Alerts Banner ── */}
      {MOCK_ALERTS.filter(a => a.severity === 'danger').length > 0 && (
        <div className="mb-5 flex items-center gap-3 bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700">
          <AlertTriangle size={16} className="flex-shrink-0 text-red-500" />
          <span><strong>{MOCK_ALERTS.filter(a => a.severity === 'danger').length} critical alert{MOCK_ALERTS.filter(a => a.severity === 'danger').length > 1 ? 's' : ''}</strong> require immediate attention — overdue deliveries or invoice mismatches detected.</span>
          <Link href="/procurement/approvals" className="ml-auto text-xs font-semibold text-red-600 hover:underline flex-shrink-0">Review</Link>
        </div>
      )}

      {/* ── KPI Grid ── */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3 mb-6">
        <KpiCard title="Purchase Requisitions" value={MOCK_REQUISITIONS.length} sub="This period" icon={ClipboardList} iconColor="bg-blue-500" href="/procurement/requisitions" trend="up" trendValue="+2 this week" />
        <KpiCard title="Pending Approvals" value={pendingApprovals} sub="Awaiting action" icon={Clock} iconColor="bg-amber-500" href="/procurement/approvals" highlight={pendingApprovals > 0} trend={pendingApprovals > 0 ? 'down' : 'neutral'} trendValue={pendingApprovals > 0 ? 'Action needed' : 'All clear'} />
        <KpiCard title="Open Purchase Orders" value={openPOs} sub="Active orders" icon={ShoppingCart} iconColor="bg-indigo-500" href="/procurement/purchase-orders" trend="neutral" trendValue="4 this month" />
        <KpiCard title="Partial Deliveries" value={partialDeliveries} sub="Outstanding" icon={Truck} iconColor="bg-orange-500" href="/procurement/goods-received" highlight={partialDeliveries > 0} />
        <KpiCard title="Procurement Spend" value="UGX 31.1M" sub="September 2026" icon={DollarSign} iconColor="bg-green-600" trend="up" trendValue="+12.7% vs Aug" />
        <KpiCard title="Unpaid Invoices" value={pendingInvoices} sub="Awaiting payment" icon={Receipt} iconColor="bg-red-500" href="/procurement/invoices" highlight={pendingInvoices > 0} />
      </div>

      {/* ── Quick Actions ── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        {[
          { label: 'New Requisition', icon: Plus, href: '/procurement/requisitions/new', color: 'bg-orange-600 hover:bg-orange-700 text-white' },
          { label: 'Create RFQ', icon: FileSearch, href: '/procurement/rfqs', color: 'bg-purple-600 hover:bg-purple-700 text-white' },
          { label: 'Purchase Orders', icon: ShoppingCart, href: '/procurement/purchase-orders', color: 'bg-indigo-600 hover:bg-indigo-700 text-white' },
          { label: 'Receive Goods', icon: Package, href: '/procurement/goods-received/new', color: 'bg-teal-600 hover:bg-teal-700 text-white' },
        ].map(action => {
          const ActionIcon = action.icon;
          return (
            <Link key={action.label} href={action.href} className={`flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-semibold transition-colors ${action.color}`}>
              <ActionIcon size={15} />
              {action.label}
            </Link>
          );
        })}
      </div>

      {/* ── Charts Row ── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">

        {/* Spend Trend */}
        <div className="lg:col-span-2 bg-white rounded-2xl border border-gray-100 p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-sm font-bold text-gray-900">Procurement Spend Trend</h3>
              <p className="text-xs text-gray-400 mt-0.5">Monthly spend — Idudi Poultry Farm</p>
            </div>
            <span className="text-xs text-gray-400">Apr – Sep 2026</span>
          </div>
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={SPEND_TREND}>
              <defs>
                <linearGradient id="spendGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#f97316" stopOpacity={0.15} />
                  <stop offset="95%" stopColor="#f97316" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} tickFormatter={v => `${(v / 1000000).toFixed(0)}M`} />
              <Tooltip formatter={(v: number) => [`UGX ${v.toLocaleString()}`, 'Spend']} contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e2e8f0' }} />
              <Area type="monotone" dataKey="spend" stroke="#f97316" strokeWidth={2} fill="url(#spendGrad)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Spend by Category */}
        <div className="bg-white rounded-2xl border border-gray-100 p-5">
          <h3 className="text-sm font-bold text-gray-900 mb-1">Spend by Category</h3>
          <p className="text-xs text-gray-400 mb-4">September 2026</p>
          <ResponsiveContainer width="100%" height={160}>
            <PieChart>
              <Pie data={SPEND_BY_CATEGORY} cx="50%" cy="50%" innerRadius={45} outerRadius={70} paddingAngle={3} dataKey="value">
                {SPEND_BY_CATEGORY.map((entry, i) => <Cell key={i} fill={entry.color} />)}
              </Pie>
              <Tooltip formatter={(v: number) => [`UGX ${v.toLocaleString()}`, '']} contentStyle={{ fontSize: 11, borderRadius: 8 }} />
            </PieChart>
          </ResponsiveContainer>
          <div className="space-y-1.5 mt-2">
            {SPEND_BY_CATEGORY.map(cat => (
              <div key={cat.name} className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: cat.color }} />
                  <span className="text-gray-600">{cat.name}</span>
                </div>
                <span className="font-semibold text-gray-800">{(cat.value / 1000000).toFixed(1)}M</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Bottom Row ── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">

        {/* Recent Requisitions */}
        <div className="bg-white rounded-2xl border border-gray-100 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-bold text-gray-900">Recent Requisitions</h3>
            <Link href="/procurement/requisitions" className="text-xs text-orange-600 font-semibold hover:underline flex items-center gap-0.5">
              View all <ArrowUpRight size={11} />
            </Link>
          </div>
          <div className="space-y-2">
            {MOCK_REQUISITIONS.slice(0, 4).map(req => {
              const st = REQUISITION_STATUS_STYLES[req.status];
              return (
                <Link key={req.id} href={`/procurement/requisitions`} className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-gray-50 transition-colors group">
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-bold text-gray-800 truncate">{req.requisitionNumber}</div>
                    <div className="text-xs text-gray-400 truncate">{req.department} · {req.requestedBy}</div>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${st.bg} ${st.text}`}>{st.label}</span>
                    <span className="text-xs text-gray-400">{formatUGX(req.estimatedTotal)}</span>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>

        {/* Recent Purchase Orders */}
        <div className="bg-white rounded-2xl border border-gray-100 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-bold text-gray-900">Purchase Orders</h3>
            <Link href="/procurement/purchase-orders" className="text-xs text-orange-600 font-semibold hover:underline flex items-center gap-0.5">
              View all <ArrowUpRight size={11} />
            </Link>
          </div>
          <div className="space-y-2">
            {MOCK_PURCHASE_ORDERS.map(po => {
              const st = PO_STATUS_STYLES[po.status];
              return (
                <Link key={po.id} href="/procurement/purchase-orders" className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-gray-50 transition-colors">
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-bold text-gray-800 truncate">{po.poNumber}</div>
                    <div className="text-xs text-gray-400 truncate">{po.supplier}</div>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${st.bg} ${st.text}`}>{st.label}</span>
                    <span className="text-xs text-gray-400">{formatUGX(po.grandTotal)}</span>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>

        {/* Alerts */}
        <div className="bg-white rounded-2xl border border-gray-100 p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Bell size={14} className="text-amber-500" />
              <h3 className="text-sm font-bold text-gray-900">Procurement Alerts</h3>
            </div>
            <span className="text-xs bg-red-100 text-red-600 font-bold px-2 py-0.5 rounded-full">{MOCK_ALERTS.length}</span>
          </div>
          <div className="space-y-2">
            {MOCK_ALERTS.map(alert => <AlertCard key={alert.id} alert={alert} />)}
          </div>
        </div>
      </div>

      {/* ── Supplier Spend Bar ── */}
      <div className="mt-4 bg-white rounded-2xl border border-gray-100 p-5">
        <h3 className="text-sm font-bold text-gray-900 mb-4">Spend by Supplier — September 2026</h3>
        <ResponsiveContainer width="100%" height={120}>
          <BarChart data={SUPPLIER_SPEND} layout="vertical" margin={{ left: 0, right: 20 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
            <XAxis type="number" tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} tickFormatter={v => `${(v / 1000000).toFixed(0)}M`} />
            <YAxis type="category" dataKey="supplier" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} width={70} />
            <Tooltip formatter={(v: number) => [`UGX ${v.toLocaleString()}`, 'Spend']} contentStyle={{ fontSize: 12, borderRadius: 8 }} />
            <Bar dataKey="spend" fill="#f97316" radius={[0, 6, 6, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

    </div>
  );
}
